import {memo} from 'react'
import {cn} from '@/shared/lib/cn'
import {useFocusHintFirstTime} from '@/pages/room/ui/focus-hint'
import {RoomControlsBar} from './room-controls-bar'
import {VideoGrid} from './video-grid'
import {SpeakingProvider} from '../speaking-context'
import {useChatPanel, ChatSidebar} from '../chat'

interface RoomContentProps {
  roomId: string
  onRequestFocusHint?: () => void
}

/**
 * Main room content - must be rendered inside LiveKitRoom provider.
 */
export const RoomContent = memo(function RoomContent({
  roomId,
  onRequestFocusHint,
}: RoomContentProps) {
  useFocusHintFirstTime(onRequestFocusHint)
  const {isOpen, unreadCount, chatMessages, send, isSending, toggleChat, closeChat} = useChatPanel()

  return (
    <SpeakingProvider>
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex flex-1 min-h-0 justify-center md:pt-0 md:p-6">
          <div className="flex w-full max-w-7xl min-h-0 flex-col">
            <div className={cn('flex flex-1 min-h-0', isOpen && 'gap-2')}>
              <div className="@container flex flex-1 min-h-0 flex-col gap-1 overflow-hidden md:rounded-lg bg-card/95 sm:p-3 sm:pt-0 md:p-4">
                <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                  <VideoGrid />
                </div>
                <div className="shrink-0">
                  <RoomControlsBar
                    roomId={roomId}
                    isChatOpen={isOpen}
                    unreadCount={unreadCount}
                    onToggleChat={toggleChat}
                  />
                </div>
              </div>

              <div
                className={cn(
                  'hidden md:flex shrink-0 min-h-0 flex-col overflow-hidden transition-[width] duration-300 ease-out sm:rounded-lg',
                  isOpen ? 'w-80' : 'w-0',
                )}
              >
                <ChatSidebar
                  messages={chatMessages}
                  onSend={send}
                  isSending={isSending}
                  onClose={closeChat}
                  isOpen={isOpen}
                  className="flex h-full min-h-0 w-80 sm:rounded-lg transition-transform duration-300 ease-out"
                  slideFromRight
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className={cn(
          'md:hidden fixed inset-0 z-40 bg-background/80 transition-opacity duration-300',
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none',
        )}
        aria-hidden
        onClick={closeChat}
      />
      <div
        className={cn(
          'md:hidden fixed inset-y-0 right-0 z-50 w-full max-w-[min(30rem,100vw)] transition-transform duration-300 ease-out rounded-l-xl',
          isOpen ? 'translate-x-0' : 'translate-x-full',
        )}
      >
        <ChatSidebar
          messages={chatMessages}
          onSend={send}
          isSending={isSending}
          onClose={closeChat}
          isOpen={isOpen}
          className="flex h-full min-h-0"
        />
      </div>
    </SpeakingProvider>
  )
})
